import { type ChangeEventHandler, type FC } from 'react';
import { AxiosError } from 'axios';

import { IconInfoCircle, IconLoader2 } from '@tabler/icons-react';
import {
  Accordion,
  AccordionItem,
  AccordionItemBody,
  AccordionItemHeader,
  InputField,
  SelectField,
  Tooltip,
  TooltipBody,
} from '@wk/components-react16';

import { ENVIRONMENTS } from '@/config/constants';
import type { ErrorResponse } from '@/types';
import type { Checkpoint } from '@/domains/checkpoint/checkpoint.types';

import type { RunEvaluationApplication } from './RunEvaluationDialog.types';

interface RunEvaluationDialogBodyProps {
  isLoading: boolean;
  latestConfiguration: unknown;
  application: RunEvaluationApplication | null;
  appConfigError: AxiosError<ErrorResponse> | null;
  values: Record<string, string | null>;
  handleChange: ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >;
  runtimeParametersByCheckpoint: Checkpoint[];
  configTimeParametersByCheckpoint: Checkpoint[];
}

const RunEvaluationDialogBody: FC<RunEvaluationDialogBodyProps> = ({
  isLoading,
  latestConfiguration,
  appConfigError,
  values,
  handleChange,
  runtimeParametersByCheckpoint,
  configTimeParametersByCheckpoint,
}) => {
  if (isLoading) {
    return (
      <div className="flex h-full min-h-40 items-center justify-center">
        <IconLoader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (!latestConfiguration || appConfigError) {
    return (
      <div className="flex items-center justify-center">
        <p className="text-center text-sm text-gray-500">
          {appConfigError?.response?.data.title ||
            `No configuration found for this application. Please create and
                publish a configuration first.`}
        </p>
      </div>
    );
  }

  return (
    <>
      <SelectField label="Environment">
        <select
          name="environment"
          value={values.environment || ''}
          onChange={handleChange}
        >
          {ENVIRONMENTS.filter((env) => env !== 'All').map((env) => (
            <option key={env} value={env}>
              {env}
            </option>
          ))}
        </select>
      </SelectField>

      {runtimeParametersByCheckpoint.map((checkpoint) => (
        <div key={checkpoint.id} className="flex flex-col gap-2">
          <p className="mb-2 text-xs font-semibold">
            {checkpoint.displayName || checkpoint.id}
          </p>
          <div className="flex flex-col gap-2 pl-4">
            {checkpoint.parameters.map((parameter) => (
              <InputField
                status={
                  !parameter.isOptional &&
                  checkpoint.isScored &&
                  !values[parameter.paramName]
                    ? 'error'
                    : undefined
                }
                errors={
                  !parameter.isOptional &&
                  checkpoint.isScored &&
                  !values[parameter.paramName]
                    ? [
                        {
                          label: 'This parameter is required',
                          labelId: `checkpoint.id-${parameter.paramName}-required`,
                        },
                      ]
                    : []
                }
                key={parameter.paramName}
                label={parameter.displayName || parameter.paramName}
              >
                <input
                  name={parameter.paramName}
                  value={values[parameter.paramName] || ''}
                  onChange={handleChange}
                />
              </InputField>
            ))}
          </div>
        </div>
      ))}

      <div>
        <Accordion>
          <AccordionItem onUserRequest={() => {}}>
            <AccordionItemHeader slot="accordionItemHeader">
              Other parameters
              <Tooltip position="right" targetSelector="#config-param-info">
                <TooltipBody slot="tooltipBody">
                  These are the parameters specified during configuration.
                </TooltipBody>
              </Tooltip>
              <span id="config-param-info">
                <IconInfoCircle className="ml-2" size={16} />
              </span>
            </AccordionItemHeader>
            <AccordionItemBody slot="accordionItemBody">
              {configTimeParametersByCheckpoint.map((checkpoint) => (
                <div key={checkpoint.id} className="flex flex-col gap-2">
                  <p className="text-xs font-semibold">
                    {checkpoint.displayName || checkpoint.id}
                  </p>
                  <div className="flex flex-col gap-2 pl-4">
                    {checkpoint.parameters.map((parameter) => (
                      <InputField
                        isDisabled
                        key={parameter.paramName}
                        label={parameter.displayName || parameter.paramName}
                        status={
                          !parameter.isOptional &&
                          checkpoint.isScored &&
                          !values[parameter.paramName]
                            ? 'error'
                            : undefined
                        }
                        errors={
                          !parameter.isOptional &&
                          checkpoint.isScored &&
                          !values[parameter.paramName]
                            ? [
                                {
                                  label: 'This parameter is required',
                                  labelId: `checkpoint.id-${parameter.paramName}-required`,
                                },
                              ]
                            : []
                        }
                      >
                        <input
                          disabled
                          name={parameter.paramName}
                          value={values[parameter.paramName] || ''}
                        />
                      </InputField>
                    ))}
                  </div>
                </div>
              ))}
            </AccordionItemBody>
          </AccordionItem>
        </Accordion>
      </div>
    </>
  );
};

export default RunEvaluationDialogBody;
